// Memory index lifecycle (F07): versions (build → evaluate → compare → activate with the
// optimistic `expected_active_version_id`, retire, rollback), evaluation datasets (frozen or
// retired, never edited in place) and evaluations with their `report_hash`. Evaluations run
// asynchronously (202 + operation) and are polled until they settle.
import { useEffect, useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import {
  activateIndexVersion,
  buildIndexVersion,
  compareIndexVersion,
  createMemoryDataset,
  evaluateIndexVersion,
  listIndexVersions,
  listMemoryDatasets,
  listMemoryEvaluations,
  retireIndexVersion,
  rollbackIndexVersion,
  runMemoryEvaluation,
  setMemoryDatasetState,
} from "../../api/endpoints-wave3.ts";
import { isApiError } from "../../api/client.ts";
import { MEMORY_BUILD_STRATEGIES, type IndexVersionStatus, type MemoryComparison, type MemoryDataset, type MemoryDatasetCase, type MemoryEvaluation, type MemoryIndexVersion } from "../../api/types-wave3.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useOperation } from "../../lib/useOperation.ts";
import { Button } from "../../components/Button.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { SelectField, TextAreaField, TextField } from "../../components/Field.tsx";
import { Banner, EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { LayersIcon } from "../../components/icons.tsx";
import { ConfirmDialog, Hash, ShortId, StatePill, useProjectRole } from "../wave3/common.tsx";

const VERSION_VIEW: Record<IndexVersionStatus, { tone: "success" | "warning" | "neutral" | "danger" | "info" | "running"; label: MessageKey }> = {
  building: { tone: "running", label: "memIndex.building" },
  ready: { tone: "info", label: "memIndex.ready" },
  active: { tone: "success", label: "memIndex.active" },
  retired: { tone: "neutral", label: "memIndex.retired" },
  failed: { tone: "danger", label: "memIndex.failed" },
};

const pct = (v: number | null) => (v === null ? "—" : `${(v * 100).toFixed(1)}%`);
const signed = (v: number | null, asPct = true) => (v === null ? "—" : `${v > 0 ? "+" : ""}${asPct ? `${(v * 100).toFixed(1)} pp` : v}`);

export function MemoryIndexPanel({ projectId }: { projectId: string }) {
  const versions = useAsync((s) => listIndexVersions(projectId, s), [projectId]);
  const datasets = useAsync((s) => listMemoryDatasets(projectId, s), [projectId]);
  const evaluations = useAsync((s) => listMemoryEvaluations(projectId, {}, s), [projectId]);
  const reloadAll = () => {
    versions.reload();
    evaluations.reload();
  };
  return (
    <div className="section-stack">
      <IndexVersions projectId={projectId} versions={versions.data?.items ?? null} error={versions.error} datasets={datasets.data?.items ?? []} onReload={versions.reload} onEvaluated={reloadAll} />
      <Datasets projectId={projectId} datasets={datasets.data?.items ?? null} error={datasets.error} onReload={datasets.reload} />
      <Evaluations
        projectId={projectId}
        items={evaluations.data?.items ?? null}
        error={evaluations.error}
        datasets={datasets.data?.items ?? []}
        versions={versions.data?.items ?? []}
        onReload={evaluations.reload}
      />
    </div>
  );
}

function IndexVersions({
  projectId,
  versions,
  error,
  datasets,
  onReload,
  onEvaluated,
}: {
  projectId: string;
  versions: MemoryIndexVersion[] | null;
  error: unknown;
  datasets: MemoryDataset[];
  onReload: () => void;
  onEvaluated: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const role = useProjectRole(projectId);
  const maintainer = role.atLeast("maintainer");
  const [strategy, setStrategy] = useState("text");
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [evaluating, setEvaluating] = useState<MemoryIndexVersion | null>(null);
  const [comparing, setComparing] = useState<MemoryIndexVersion | null>(null);
  const [retiring, setRetiring] = useState<MemoryIndexVersion | null>(null);
  const active = versions?.find((v) => v.status === "active") ?? null;

  const act = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      toast(success, "success");
      onReload();
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card" aria-labelledby={`index-versions-${projectId}`} data-testid="index-versions">
      <div className="card-header">
        <LayersIcon size={18} />
        <h2 id={`index-versions-${projectId}`} className="card-title" style={{ flex: 1 }}>
          {t("memIndex.title")}
        </h2>
        {active ? (
          <span className="subtle">
            {t("memIndex.serving")} <Hash value={active.id} />
          </span>
        ) : (
          <span className="subtle">{t("memIndex.noneServing")}</span>
        )}
      </div>
      <div className="card-body stack">
        <p className="field-hint">{t("memIndex.intro")}</p>
        <ActionError error={actionError} onDismiss={() => setActionError(null)} onReload={isApiError(actionError) && actionError.code === "version_conflict" ? onReload : undefined} />
        {error && !versions ? (
          <ErrorBanner error={error} onRetry={onReload} />
        ) : versions === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : versions.length === 0 ? (
          <EmptyState icon={<LayersIcon />} title={t("memIndex.emptyTitle")} headingLevel={3}>
            <p>{t("memIndex.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("memIndex.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("memIndex.version")}</th>
                  <th scope="col">{t("memIndex.status")}</th>
                  <th scope="col">{t("memory.strategy")}</th>
                  <th scope="col">{t("memIndex.dates")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => {
                  const view = VERSION_VIEW[v.status];
                  const searchable = v.status === "ready" || v.status === "active" || v.status === "retired";
                  return (
                    <tr key={v.id} data-testid="index-version-row" data-status={v.status}>
                      <td>
                        <Hash value={v.id} />
                        <span className="subtle" style={{ display: "block" }}>
                          {t("memIndex.chunking", { v: v.chunking_version })}
                        </span>
                      </td>
                      <td>
                        <StatePill tone={view.tone} label={t(view.label)} />
                        {v.serving ? (
                          <span className="badge badge--accent" style={{ marginLeft: 4 }}>
                            {t("memIndex.servingBadge")}
                          </span>
                        ) : null}
                        {v.failed_reason ? (
                          <span className="subtle user-content" style={{ display: "block" }}>
                            {v.failed_reason}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <span className="mono">{v.strategy}</span>
                        <span className="subtle" style={{ display: "block" }}>
                          {v.embedder ? `${v.embedder.provider}/${v.embedder.model}` : t("memIndex.noEmbedder")}
                        </span>
                      </td>
                      <td className="subtle">
                        {t("memIndex.createdAt", { when: formatDateTime(v.created_at) })}
                        {v.activated_at ? <span style={{ display: "block" }}>{t("memIndex.activatedAt", { when: formatDateTime(v.activated_at) })}</span> : null}
                        {v.retired_at ? <span style={{ display: "block" }}>{t("memIndex.retiredAt", { when: formatDateTime(v.retired_at) })}</span> : null}
                      </td>
                      <td>
                        <div className="row row--wrap">
                          {maintainer && searchable ? (
                            <Button size="sm" onClick={() => setEvaluating(v)} disabled={datasets.length === 0} title={datasets.length === 0 ? t("memIndex.needDataset") : undefined}>
                              {t("memIndex.evaluate")}
                            </Button>
                          ) : null}
                          {searchable ? (
                            <Button size="sm" onClick={() => setComparing(v)} disabled={datasets.length === 0}>
                              {t("memIndex.compare")}
                            </Button>
                          ) : null}
                          {maintainer && v.status === "ready" ? (
                            <Button
                              size="sm"
                              variant="primary"
                              loading={busy === `activate:${v.id}`}
                              onClick={() => void act(`activate:${v.id}`, () => activateIndexVersion(v.id, active?.id ?? null), t("memIndex.activated"))}
                            >
                              {t("memIndex.activate")}
                            </Button>
                          ) : null}
                          {maintainer && (v.status === "ready" || v.status === "failed") ? (
                            <Button size="sm" variant="ghost" onClick={() => setRetiring(v)}>
                              {t("memIndex.retire")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {maintainer ? (
          <div className="field-row field-row--compact">
            <SelectField label={t("memIndex.buildStrategy")} value={strategy} onChange={(e) => setStrategy(e.target.value)} hint={t("memIndex.buildHint")}>
              {MEMORY_BUILD_STRATEGIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SelectField>
            <Button variant="primary" loading={busy === "build"} onClick={() => void act("build", () => buildIndexVersion(projectId, { strategy }), t("memIndex.built"))}>
              {t("memIndex.build")}
            </Button>
            {active && active.previous_active_id ? (
              <Button loading={busy === "rollback"} onClick={() => void act("rollback", () => rollbackIndexVersion(projectId, active.id), t("memIndex.rolledBack"))}>
                {t("memIndex.rollback")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <EvaluateDialog version={evaluating} datasets={datasets} onClose={() => setEvaluating(null)} onSettled={onEvaluated} />
      <CompareDialog version={comparing} versions={versions ?? []} datasets={datasets} onClose={() => setComparing(null)} />
      <ConfirmDialog
        open={retiring !== null}
        title={t("memIndex.retireTitle")}
        body={<p>{t("memIndex.retireBody")}</p>}
        confirmLabel={t("memIndex.retire")}
        danger
        onClose={() => setRetiring(null)}
        onConfirm={async () => {
          if (retiring) await retireIndexVersion(retiring.id);
          onReload();
        }}
      />
    </section>
  );
}

function EvaluateDialog({ version, datasets, onClose, onSettled }: { version: MemoryIndexVersion | null; datasets: MemoryDataset[]; onClose: () => void; onSettled: () => void }) {
  const { t } = useI18n();
  const [dataset, setDataset] = useState("");
  const [k, setK] = useState("10");
  const { op, error, busy, run, reset } = useOperation();
  useEffect(() => {
    if (version) {
      reset();
      setDataset(datasets.find((d) => d.state !== "retired")?.id ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
  useEffect(() => {
    if (op && (op.status === "succeeded" || op.status === "failed")) onSettled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op?.status]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!version || !dataset) return;
    void run(() => evaluateIndexVersion(version.id, { dataset_id: dataset, k: Math.min(50, Math.max(1, Number(k) || 10)) }));
  };
  const result = (op?.result ?? null) as { report_hash?: string; recall_at_k?: number | null; contamination_rate?: number | null; stale_as_current?: number | null } | null;
  return (
    <Dialog open={version !== null} onClose={onClose} title={t("memIndex.evaluateTitle")} busy={busy && !op}>
      <form className="stack" onSubmit={submit} noValidate data-testid="evaluate-form">
        <p className="subtle">
          {t("memIndex.version")} <Hash value={version?.id} />
        </p>
        <SelectField label={t("memEval.dataset")} value={dataset} onChange={(e) => setDataset(e.target.value)}>
          {datasets
            .filter((d) => d.state !== "retired")
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} v{d.version} ({t(`memDataset.${d.state}`)})
              </option>
            ))}
        </SelectField>
        <TextField label={t("memory.k")} type="number" min={1} max={50} value={k} onChange={(e) => setK(e.target.value)} />
        <ActionError error={error} />
        {op ? (
          <div role="status" aria-live="polite" data-testid="evaluation-outcome" data-status={op.status}>
            {op.status === "accepted" || op.status === "running" ? (
              <Pill tone="info" icon="spinner">
                {t("memEval.running")}
              </Pill>
            ) : op.status === "failed" ? (
              <ActionError error={new Error(op.error?.message ?? t("memEval.failed"))} title={t("memEval.failed")} />
            ) : (
              <dl className="kv">
                <div>
                  <dt>{t("memEval.recall")}</dt>
                  <dd>{pct(result?.recall_at_k ?? null)}</dd>
                </div>
                <div>
                  <dt>{t("memEval.contamination")}</dt>
                  <dd>{pct(result?.contamination_rate ?? null)}</dd>
                </div>
                <div>
                  <dt>{t("memEval.staleAsCurrent")}</dt>
                  <dd>{result?.stale_as_current ?? "—"}</dd>
                </div>
                <div>
                  <dt>{t("memEval.reportHash")}</dt>
                  <dd>
                    <Hash value={result?.report_hash ?? null} />
                  </dd>
                </div>
              </dl>
            )}
          </div>
        ) : null}
        <div className="row row--wrap dialog-actions">
          <Button onClick={onClose}>{t("common.close")}</Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!dataset}>
            {t("memIndex.evaluate")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CompareDialog({ version, versions, datasets, onClose }: { version: MemoryIndexVersion | null; versions: MemoryIndexVersion[]; datasets: MemoryDataset[]; onClose: () => void }) {
  const { t } = useI18n();
  const [dataset, setDataset] = useState("");
  const [baseline, setBaseline] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<MemoryComparison | null>(null);
  useEffect(() => {
    if (version) {
      setDataset(datasets[0]?.id ?? "");
      setBaseline("");
      setError(null);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!version || !dataset) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await compareIndexVersion(version.id, { dataset_id: dataset, ...(baseline ? { baseline_index_version_id: baseline } : {}) }));
    } catch (err) {
      setError(err);
      setResult(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={version !== null} onClose={onClose} title={t("memIndex.compareTitle")} busy={busy} wide>
      <form className="stack" onSubmit={submit} noValidate data-testid="compare-form">
        <p className="subtle">
          {t("memIndex.version")} <Hash value={version?.id} />
        </p>
        <div className="field-row field-row--compact">
          <SelectField label={t("memEval.dataset")} value={dataset} onChange={(e) => setDataset(e.target.value)}>
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} v{d.version}
              </option>
            ))}
          </SelectField>
          <SelectField label={t("memIndex.baseline")} value={baseline} onChange={(e) => setBaseline(e.target.value)}>
            <option value="">{t("memIndex.baselineActive")}</option>
            {versions
              .filter((v) => v.id !== version?.id)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.id.slice(0, 8)} ({t(VERSION_VIEW[v.status].label)})
                </option>
              ))}
          </SelectField>
          <Button type="submit" variant="primary" loading={busy}>
            {t("memIndex.compare")}
          </Button>
        </div>
        <ActionError error={error} />
        {result ? (
          <div className="stack" data-testid="compare-result" data-promotable={String(result.promotable)}>
            <div className="table-wrap">
              <table className="table">
                <caption className="sr-only">{t("memIndex.compareTitle")}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t("memEval.metric")}</th>
                    <th scope="col">{t("memIndex.target")}</th>
                    <th scope="col">{t("memIndex.baseline")}</th>
                    <th scope="col">{t("memIndex.delta")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">{t("memEval.recall")}</th>
                    <td>{pct(result.target.recall_at_k)}</td>
                    <td>{pct(result.baseline?.recall_at_k ?? null)}</td>
                    <td>{signed(result.delta.recall_at_k)}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t("memEval.contamination")}</th>
                    <td>{pct(result.target.contamination_rate)}</td>
                    <td>{pct(result.baseline?.contamination_rate ?? null)}</td>
                    <td>{signed(result.delta.contamination_rate)}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t("memEval.staleAsCurrent")}</th>
                    <td>{result.target.stale_as_current ?? "—"}</td>
                    <td>{result.baseline?.stale_as_current ?? "—"}</td>
                    <td>{signed(result.delta.stale_as_current, false)}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t("memEval.reportHash")}</th>
                    <td>
                      <Hash value={result.target.report_hash} />
                    </td>
                    <td>
                      <Hash value={result.baseline?.report_hash ?? null} />
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
            {result.promotable ? (
              <Banner tone="info" title={t("memIndex.promotable")}>
                <p>{t("memIndex.promotableBody")}</p>
              </Banner>
            ) : (
              <Banner tone="warning" title={t("memIndex.notPromotable")}>
                <ul className="notes">
                  {result.failures.map((f, i) => (
                    <li key={i} className="user-content">
                      {f}
                    </li>
                  ))}
                </ul>
              </Banner>
            )}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

const DATASET_EXAMPLE = JSON.stringify(
  [
    { case_key: "auth-001", question: "How are sessions renewed?", expected: [{ source_key: "decision:session-renewal" }], forbidden_source_keys: [], stale_source_keys: [] },
  ],
  null,
  2,
);

function Datasets({ projectId, datasets, error, onReload }: { projectId: string; datasets: MemoryDataset[] | null; error: unknown; onReload: () => void }) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const role = useProjectRole(projectId);
  const maintainer = role.atLeast("maintainer");
  const [name, setName] = useState("");
  const [cases, setCases] = useState(DATASET_EXAMPLE);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    let parsed: MemoryDatasetCase[] | null = null;
    if (!name.trim()) errs["name"] = t("memDataset.nameRequired");
    try {
      const v: unknown = JSON.parse(cases);
      if (Array.isArray(v) && v.length > 0) parsed = v as MemoryDatasetCase[];
      else errs["cases"] = t("memDataset.casesArray");
    } catch {
      errs["cases"] = t("memDataset.casesJson");
    }
    setLocal(errs);
    if (Object.keys(errs).length > 0 || !parsed) return;
    setBusy("create");
    setActionError(null);
    try {
      await createMemoryDataset(projectId, { name: name.trim(), cases: parsed });
      setName("");
      toast(t("memDataset.created"), "success");
      onReload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  };
  const setState = async (d: MemoryDataset, state: "frozen" | "retired") => {
    setBusy(`${d.id}:${state}`);
    setActionError(null);
    try {
      await setMemoryDatasetState(d.id, state);
      onReload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card" aria-labelledby={`datasets-${projectId}`} data-testid="memory-datasets">
      <div className="card-header">
        <h2 id={`datasets-${projectId}`} className="card-title">
          {t("memDataset.title")}
        </h2>
      </div>
      <div className="card-body stack">
        <ActionError error={actionError} onDismiss={() => setActionError(null)} />
        {error && !datasets ? (
          <ErrorBanner error={error} onRetry={onReload} />
        ) : datasets === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : datasets.length === 0 ? (
          <p className="subtle">{t("memDataset.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("memDataset.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("memDataset.name")}</th>
                  <th scope="col">{t("inbox.col.state")}</th>
                  <th scope="col">{t("memDataset.cases")}</th>
                  <th scope="col">{t("memDataset.hash")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {datasets.map((d) => (
                  <tr key={d.id} data-testid="dataset-row" data-state={d.state}>
                    <td>
                      {d.name} <span className="subtle">v{d.version}</span>
                      <span className="subtle" style={{ display: "block" }}>
                        {formatDateTime(d.created_at)}
                      </span>
                    </td>
                    <td>
                      <StatePill tone={d.state === "frozen" ? "success" : d.state === "draft" ? "warning" : "neutral"} label={t(`memDataset.${d.state}`)} />
                    </td>
                    <td>{d.case_count}</td>
                    <td>
                      <Hash value={d.body_hash} />
                    </td>
                    <td>
                      {maintainer ? (
                        <div className="row row--wrap">
                          {d.state === "draft" ? (
                            <Button size="sm" onClick={() => void setState(d, "frozen")} loading={busy === `${d.id}:frozen`}>
                              {t("memDataset.freeze")}
                            </Button>
                          ) : null}
                          {d.state !== "retired" ? (
                            <Button size="sm" variant="ghost" onClick={() => void setState(d, "retired")} loading={busy === `${d.id}:retired`}>
                              {t("memDataset.retire")}
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {maintainer ? (
          <details className="disclosure">
            <summary>{t("memDataset.add")}</summary>
            <form className="stack" onSubmit={create} noValidate data-testid="dataset-form">
              <TextField label={t("memDataset.name")} value={name} onChange={(e) => setName(e.target.value)} error={local["name"] ?? null} maxLength={200} />
              <TextAreaField label={t("memDataset.casesLabel")} hint={t("memDataset.casesHint")} value={cases} onChange={(e) => setCases(e.target.value)} error={local["cases"] ?? null} rows={10} spellCheck={false} className="mono" />
              <div className="row">
                <Button type="submit" variant="primary" loading={busy === "create"}>
                  {t("memDataset.create")}
                </Button>
              </div>
            </form>
          </details>
        ) : null}
      </div>
    </section>
  );
}

function Evaluations({
  projectId,
  items,
  error,
  datasets,
  versions,
  onReload,
}: {
  projectId: string;
  items: MemoryEvaluation[] | null;
  error: unknown;
  datasets: MemoryDataset[];
  versions: MemoryIndexVersion[];
  onReload: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const role = useProjectRole(projectId);
  const [dataset, setDataset] = useState("");
  const { op, error: runError, busy, run } = useOperation();
  useEffect(() => {
    if (op && (op.status === "succeeded" || op.status === "failed")) onReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op?.status]);
  const datasetName = (id: string) => {
    const d = datasets.find((x) => x.id === id);
    return d ? `${d.name} v${d.version}` : null;
  };
  const active = versions.find((v) => v.status === "active");
  const chosen = dataset || datasets.find((d) => d.state !== "retired")?.id || "";
  return (
    <section className="card" aria-labelledby={`evaluations-${projectId}`} data-testid="memory-evaluations">
      <div className="card-header">
        <h2 id={`evaluations-${projectId}`} className="card-title" style={{ flex: 1 }}>
          {t("memEval.title")}
        </h2>
      </div>
      <div className="card-body stack">
        {role.atLeast("maintainer") ? (
          <form
            className="field-row field-row--compact"
            onSubmit={(e) => {
              e.preventDefault();
              if (chosen) void run(() => runMemoryEvaluation(projectId, { dataset_id: chosen, k: 10 }));
            }}
            aria-label={t("memEval.runActive")}
          >
            <SelectField label={t("memEval.dataset")} value={chosen} onChange={(e) => setDataset(e.target.value)} hint={active ? t("memEval.againstActive") : t("memEval.noActive")}>
              {datasets.length === 0 ? <option value="">{t("memDataset.empty")}</option> : null}
              {datasets
                .filter((d) => d.state !== "retired")
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} v{d.version}
                  </option>
                ))}
            </SelectField>
            <Button type="submit" loading={busy} disabled={!chosen}>
              {t("memEval.runActive")}
            </Button>
          </form>
        ) : null}
        <ActionError error={runError} />
        {op && (op.status === "accepted" || op.status === "running") ? (
          <Pill tone="info" icon="spinner">
            {t("memEval.running")}
          </Pill>
        ) : null}
        {error && !items ? (
          <ErrorBanner error={error} onRetry={onReload} />
        ) : items === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : items.length === 0 ? (
          <p className="subtle">{t("memEval.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("memEval.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("memEval.dataset")}</th>
                  <th scope="col">{t("memIndex.version")}</th>
                  <th scope="col">{t("inbox.col.state")}</th>
                  <th scope="col">{t("memEval.recall")}</th>
                  <th scope="col">{t("memEval.contamination")}</th>
                  <th scope="col">{t("memEval.staleAsCurrent")}</th>
                  <th scope="col">{t("memEval.reportHash")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid="evaluation-row" data-state={e.state}>
                    <td>
                      {datasetName(e.dataset_id) ?? <ShortId id={e.dataset_id} />}
                      <span className="subtle" style={{ display: "block" }}>
                        {formatDateTime(e.created_at)}
                      </span>
                    </td>
                    <td>
                      <Hash value={e.index_version_id} />
                      <span className="subtle" style={{ display: "block" }}>
                        {e.strategy} · k={e.k}
                      </span>
                    </td>
                    <td>
                      <StatePill tone={e.state === "completed" ? "success" : e.state === "failed" ? "danger" : "running"} label={t(`memEvalState.${e.state}`)} />
                    </td>
                    <td>{pct(e.recall_at_k)}</td>
                    <td>{pct(e.contamination_rate)}</td>
                    <td>{e.stale_as_current ?? "—"}</td>
                    <td>
                      <Hash value={e.report_hash} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
